import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAdminUsers } from "../hooks/useAdminUsers";
import { useAdminDashboardPresence } from "../hooks/useAdminDashboardPresence";
import { CardFrame } from "./CardFrame";

function formatCreatedAt(iso: string) {
  const d = Date.parse(iso);
  if (!Number.isFinite(d)) return iso;
  return new Date(d).toLocaleString();
}

function formatLastUpdated(ts: number | null) {
  if (!ts) return null;
  return new Date(ts).toLocaleTimeString();
}

function formatSeen(ts: number) {
  const d = Date.parse(String(ts));
  if (!Number.isFinite(d)) return "—";
  return new Date(d).toLocaleTimeString();
}

export function AdminUsersView() {
  const location = useLocation();
  const enabled = location.pathname === "/admin";
  const { users, loading, refreshing, error, lastUpdated, reload } = useAdminUsers(
    enabled,
    location.key,
  );
  const presence = useAdminDashboardPresence(enabled, location.key);
  const [pauseMessage, setPauseMessage] = useState("");
  const busy = loading || refreshing;
  const presenceBusy = presence.loading || presence.refreshing;

  const uiPaused = presence.uiLoad?.paused ?? false;

  return (
    <div className="flex flex-col gap-4">
      <CardFrame className="overflow-hidden p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
              Live dashboard sessions
            </p>
            <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
              Open browser tabs connected to the dashboard (refreshes every 15s)
              {presence.lastUpdated && !presenceBusy && (
                <span className="ml-2 text-text-tertiary">
                  · updated {formatLastUpdated(presence.lastUpdated)}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void presence.reload()}
            disabled={presenceBusy}
            className="text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            style={{ fontSize: "var(--font-micro)" }}
          >
            {presence.refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {presence.summary && (
          <p className="mb-3 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
            {presence.summary.user_count} user{presence.summary.user_count === 1 ? "" : "s"} ·{" "}
            {presence.summary.tab_count} tab{presence.summary.tab_count === 1 ? "" : "s"}
          </p>
        )}

        {presence.error && (
          <p className="text-status-error" style={{ fontSize: "var(--font-body)" }}>
            {presence.error}
          </p>
        )}

        {presenceBusy && presence.sessions.length === 0 && !presence.error && (
          <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            Loading sessions…
          </p>
        )}

        {!presence.error && presence.sessions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border-subtle text-text-secondary">
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    User
                  </th>
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Topic
                  </th>
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    OS
                  </th>
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Browser
                  </th>
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    IP
                  </th>
                  <th className="py-2 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Last seen
                  </th>
                </tr>
              </thead>
              <tbody>
                {presence.sessions.map((s) => (
                  <tr key={s.client_id} className="border-b border-border-subtle/60">
                    <td
                      className="py-2.5 pr-3 font-medium text-text-primary"
                      style={{ fontSize: "var(--font-body)" }}
                    >
                      {s.login}
                    </td>
                    <td
                      className="py-2.5 pr-3 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                      title={s.path}
                    >
                      {s.topic}
                    </td>
                    <td
                      className="py-2.5 pr-3 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {s.os}
                    </td>
                    <td
                      className="py-2.5 pr-3 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {s.browser}
                    </td>
                    <td
                      className="py-2.5 pr-3 font-mono text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {s.ip}
                    </td>
                    <td
                      className="py-2.5 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {formatSeen(s.last_seen_ms)}
                      {s.age_sec > 0 ? ` (${s.age_sec}s ago)` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!presenceBusy && !presence.error && presence.sessions.length === 0 && (
          <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            No active dashboard tabs right now.
          </p>
        )}
      </CardFrame>

      <CardFrame className="overflow-hidden p-4">
        <div className="mb-3">
          <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
            UI load control
          </p>
          <p className="text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
            Pause the dashboard for non-admin users during high server load. Admins can still use
            the dashboard and unpause here.
          </p>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={presence.savingUiLoad}
            onClick={() =>
              void presence.setUiPaused(!uiPaused, pauseMessage.trim() || undefined)
            }
            className={[
              "rounded-md px-3 py-1.5 font-medium",
              uiPaused
                ? "bg-status-ok/15 text-status-ok hover:bg-status-ok/25"
                : "bg-status-warn/15 text-status-warn hover:bg-status-warn/25",
              presence.savingUiLoad ? "cursor-not-allowed opacity-50" : "",
            ].join(" ")}
            style={{ fontSize: "var(--font-micro)" }}
          >
            {presence.savingUiLoad
              ? "Saving…"
              : uiPaused
                ? "Resume dashboard UI"
                : "Pause dashboard UI"}
          </button>
          <span
            className={uiPaused ? "text-status-warn" : "text-text-secondary"}
            style={{ fontSize: "var(--font-micro)" }}
          >
            {uiPaused ? "Paused for regular users" : "Running normally"}
            {uiPaused && presence.uiLoad?.by_login
              ? ` · by ${presence.uiLoad.by_login}`
              : ""}
          </span>
        </div>

        <label className="block">
          <span className="mb-1 block text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
            Message shown to paused users (optional)
          </span>
          <input
            type="text"
            value={pauseMessage}
            onChange={(e) => setPauseMessage(e.target.value)}
            placeholder={
              presence.uiLoad?.message ||
              "Dashboard paused due to high server load. Please try again shortly."
            }
            className="w-full max-w-xl rounded-md border border-border-subtle bg-surface-secondary px-3 py-2 text-text-primary"
            style={{ fontSize: "var(--font-body)" }}
          />
        </label>
      </CardFrame>

      <CardFrame className="overflow-hidden p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            Registered accounts
            {lastUpdated && !busy && (
              <span className="ml-2 text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
                · updated {formatLastUpdated(lastUpdated)}
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={() => void reload()}
            disabled={busy}
            className="text-accent hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            style={{ fontSize: "var(--font-micro)" }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {loading && users.length === 0 && (
          <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            Loading…
          </p>
        )}
        {error && (
          <p className="text-status-error" style={{ fontSize: "var(--font-body)" }}>
            {error}
          </p>
        )}
        {!busy && !error && users.length === 0 && (
          <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            No users yet.
          </p>
        )}
        {!error && users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border-subtle text-text-secondary">
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Login
                  </th>
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Email
                  </th>
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Roles
                  </th>
                  <th className="py-2 pr-3 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Status
                  </th>
                  <th className="py-2 font-medium" style={{ fontSize: "var(--font-micro)" }}>
                    Registered
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border-subtle/60">
                    <td
                      className="py-2.5 pr-3 font-medium text-text-primary"
                      style={{ fontSize: "var(--font-body)" }}
                    >
                      {u.login}
                    </td>
                    <td
                      className="py-2.5 pr-3 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {u.email ?? "—"}
                    </td>
                    <td
                      className="py-2.5 pr-3 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {u.roles.length ? u.roles.join(", ") : "—"}
                    </td>
                    <td
                      className="py-2.5 pr-3 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {u.disabled
                        ? "disabled"
                        : u.email && !u.email_verified
                          ? "unverified"
                          : "active"}
                    </td>
                    <td
                      className="py-2.5 text-text-secondary"
                      style={{ fontSize: "var(--font-micro)" }}
                    >
                      {formatCreatedAt(u.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && users.length > 0 && (
          <p className="mt-3 text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
            {users.length} account{users.length === 1 ? "" : "s"}
            {refreshing ? " · refreshing…" : ""}
          </p>
        )}
      </CardFrame>
    </div>
  );
}
